import { supabase } from '@/lib/supabase';
import type {
  ConsentDecision,
  ConsentSource,
  ConsentState,
  ConsentType,
} from './types';
import { CONSENT_VERSION_STALE_CODE } from './types';

/**
 * Fehler, wenn der Client eine Fassung angezeigt hat, die nicht mehr gilt.
 * Die Zustimmung bezoege sich dann auf einen ueberholten Text - der Aufruf
 * wird serverseitig abgewiesen, und die UI bittet um einen Neuladen.
 */
export class ConsentVersionStaleError extends Error {
  constructor() {
    super('Consent version is stale');
    this.name = 'ConsentVersionStaleError';
  }
}

export async function fetchConsentState(): Promise<ConsentState> {
  const { data, error } = await supabase.rpc('get_consent_state');
  if (error) throw error;
  return data as ConsentState;
}

/**
 * Protokolliert eine Entscheidung. Zeitstempel und Version setzt die
 * Datenbank; p_version geht nur mit, damit der Server bemerkt, wenn der
 * Client einen veralteten Text angezeigt hat.
 */
export async function recordConsent(params: {
  consentType: ConsentType;
  decision: ConsentDecision;
  version?: string | null;
  source?: ConsentSource;
}): Promise<void> {
  const { error } = await supabase.rpc('record_consent', {
    p_consent_type: params.consentType,
    p_decision: params.decision,
    p_version: params.version ?? null,
    p_source: params.source ?? 'gate',
  });

  if (error) {
    if (error.code === CONSENT_VERSION_STALE_CODE) {
      throw new ConsentVersionStaleError();
    }
    throw error;
  }
}

/**
 * Loescht das eigene Konto ueber delete_own_account() (Migration 136):
 * raeumt ~30 Tabellen in der richtigen Reihenfolge ab und loescht am Ende
 * auth.users. Der frueher hier liegende clientseitige Loeschpfad des
 * ConsentManagementModal hat das Profil wegen fehlender DELETE-Policy nie
 * getroffen und auth.users nie angefasst.
 */
export async function deleteOwnAccount(): Promise<void> {
  const { error } = await supabase.rpc('delete_own_account');
  if (error) throw error;
}
